import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';

const ApplicationModal = ({ job, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    coverLetter: '',
    resume: null,
    portfolio: null,
    confirmEligibility: false,
    confirmAvailability: false,
    confirmAccuracy: false
  });

  const [errors, setErrors] = useState({});

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleFileChange = (field, file) => {
    if (file && file?.size > 5 * 1024 * 1024) {
      setErrors(prev => ({ ...prev, [field]: 'File size must be less than 5MB' }));
      return;
    }
    setFormData(prev => ({ ...prev, [field]: file }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.coverLetter?.trim()) {
      newErrors.coverLetter = 'Cover letter is required';
    } else if (formData?.coverLetter?.trim()?.length < 100) {
      newErrors.coverLetter = 'Cover letter must be at least 100 characters';
    }

    if (!formData?.resume) {
      newErrors.resume = 'Resume is required';
    }

    if (!formData?.confirmEligibility) {
      newErrors.confirmEligibility = 'Please confirm you meet the eligibility criteria';
    }

    if (!formData?.confirmAvailability) {
      newErrors.confirmAvailability = 'Please confirm your availability';
    }

    if (!formData?.confirmAccuracy) {
      newErrors.confirmAccuracy = 'Please confirm the accuracy of information';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (validateForm()) {
      onSubmit(job?.id, formData);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
      <div className="w-full max-w-2xl max-h-[90vh] bg-card rounded-lg shadow-warm-xl overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 md:p-6 border-b border-border">
          <div>
            <h2 className="text-lg md:text-xl font-heading font-semibold text-card-foreground mb-1">
              Apply for {job?.title}
            </h2>
            <p className="text-sm text-muted-foreground">{job?.company}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-md hover:bg-muted transition-smooth"
            aria-label="Close modal"
          >
            <Icon name="X" size={24} color="var(--color-muted-foreground)" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 md:p-6">
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-card-foreground mb-2">
                Cover Letter <span className="text-error">*</span>
              </label>
              <textarea
                value={formData?.coverLetter}
                onChange={(e) => handleInputChange('coverLetter', e?.target?.value)}
                placeholder="Explain why you're a great fit for this position (minimum 100 characters)..."
                rows={6}
                className={`w-full px-4 py-3 bg-background border rounded-md text-sm text-card-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-smooth ${
                  errors?.coverLetter ? 'border-error' : 'border-border'
                }`}
              />
              {errors?.coverLetter && (
                <p className="mt-1 text-sm text-error">{errors?.coverLetter}</p>
              )}
              <p className="mt-1 text-xs text-muted-foreground">
                {formData?.coverLetter?.length} / 100 characters minimum
              </p>
            </div>

            <div>
              <Input
                type="file"
                label="Resume"
                description="Upload your latest resume (PDF, DOC, DOCX - Max 5MB)"
                error={errors?.resume}
                required
                onChange={(e) => handleFileChange('resume', e?.target?.files?.[0])}
                accept=".pdf,.doc,.docx"
              />
            </div>

            <div>
              <Input
                type="file"
                label="Portfolio (Optional)"
                description="Upload your portfolio or work samples (PDF, ZIP - Max 5MB)"
                error={errors?.portfolio}
                onChange={(e) => handleFileChange('portfolio', e?.target?.files?.[0])}
                accept=".pdf,.zip"
              />
            </div>

            <div className="space-y-3 p-4 bg-muted/50 rounded-lg">
              <h3 className="text-sm font-medium text-card-foreground mb-3">
                Confirmations <span className="text-error">*</span>
              </h3>
              <Checkbox
                label="I confirm that I meet all the eligibility criteria for this position"
                checked={formData?.confirmEligibility}
                onChange={(e) => handleInputChange('confirmEligibility', e?.target?.checked)}
                error={errors?.confirmEligibility}
                size="sm"
              />
              <Checkbox
                label="I am available to join within the specified timeframe"
                checked={formData?.confirmAvailability}
                onChange={(e) => handleInputChange('confirmAvailability', e?.target?.checked)}
                error={errors?.confirmAvailability}
                size="sm"
              />
              <Checkbox
                label="I confirm that all information provided is accurate and complete"
                checked={formData?.confirmAccuracy}
                onChange={(e) => handleInputChange('confirmAccuracy', e?.target?.checked)}
                error={errors?.confirmAccuracy}
                size="sm"
              />
            </div>

            <div className="p-4 bg-warning/10 border border-warning/20 rounded-lg">
              <div className="flex gap-3">
                <Icon name="AlertCircle" size={20} color="var(--color-warning)" className="flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-sm font-medium text-card-foreground mb-1">
                    Important Information
                  </h4>
                  <ul className="text-xs text-muted-foreground space-y-1">
                    <li>• Your application will be reviewed within 5-7 business days</li>
                    <li>• Ensure all documents are up-to-date and clearly readable</li>
                    <li>• You can track your application status in the Applications section</li>
                    <li>• False information may lead to application rejection</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </form>

        <div className="flex flex-col sm:flex-row gap-3 p-4 md:p-6 border-t border-border">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            fullWidth
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="default"
            onClick={handleSubmit}
            fullWidth
            iconName="Send"
            iconPosition="left"
          >
            Submit Application
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ApplicationModal;